import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { locationService } from '../../api/locationService';
import useActiveRegions from '../../hooks/useActiveRegions';
import {
  KENYA_COUNTRY_ID,
  KENYA_COUNTRY_NAME,
  locationOptionId,
  locationOptionName,
  unwrapLocationList,
} from '../../constants/kenyaLocations';

function toIdStr(value) {
  return value != null && value !== '' ? String(value) : '';
}

/**
 * Phase 1 location section: Kenya (fixed) → Region → County → Sub County.
 * Required fields; County disabled until Region; Sub County disabled until County.
 *
 * Cascade rules:
 * - Region change → filter counties, clear county + sub county
 * - County change → fetch sub counties once for that county, clear sub county
 * - Sub County change → update selection only (no county/sub-county API)
 */
const LocationSection = ({
  initialValues = {},
  onLocationChange,
  horizontal = false,
  /** 2 = compact modal/form grid; 4 or horizontal = wide row; default = stacked */
  columns,
  showErrors = false,
  errors = {},
  required = true,
  /** When false, Sub County has no * and is not treated as required in the UI. */
  subCountyRequired = false,
  /** When false, Country UI is hidden; Kenya countryId is still used internally. */
  showCountry = false,
  disabled = false,
}) => {
  const { regions, loading: loadingRegions } = useActiveRegions();
  const [counties, setCounties] = useState([]);
  const [subCounties, setSubCounties] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(String(KENYA_COUNTRY_ID));
  const [selectedRegion, setSelectedRegion] = useState(toIdStr(initialValues.regionId));
  const [selectedCounty, setSelectedCounty] = useState(toIdStr(initialValues.countyId));
  const [selectedSubCounty, setSelectedSubCounty] = useState(toIdStr(initialValues.subCountyId));

  const [loading, setLoading] = useState({ counties: false, subCounties: false });

  const selectedRegionRef = useRef(selectedRegion);
  const selectedCountyRef = useRef(selectedCounty);
  const selectedSubCountyRef = useRef(selectedSubCounty);
  selectedRegionRef.current = selectedRegion;
  selectedCountyRef.current = selectedCounty;
  selectedSubCountyRef.current = selectedSubCounty;

  const lastFetchedRegionRef = useRef('');
  const lastFetchedCountyRef = useRef('');
  const subCountiesCacheRef = useRef(new Map());
  const lastNotifiedRef = useRef('');
  const onLocationChangeRef = useRef(onLocationChange);
  onLocationChangeRef.current = onLocationChange;

  // Sync from parent only when values actually differ (ignore no-op echoes).
  useEffect(() => {
    const nextRegion = toIdStr(initialValues.regionId);
    const nextCounty = toIdStr(initialValues.countyId);
    const nextSub = toIdStr(initialValues.subCountyId);

    if (!nextRegion && !nextCounty && !nextSub) {
      return;
    }

    const regionChanged = nextRegion !== selectedRegionRef.current;
    const countyChanged = nextCounty !== selectedCountyRef.current;
    const subChanged = nextSub !== selectedSubCountyRef.current;

    if (!regionChanged && !countyChanged && !subChanged) {
      return;
    }

    if (regionChanged) {
      setSelectedRegion(nextRegion);
      setSelectedCounty(nextCounty);
      setSelectedSubCounty(nextSub);
      return;
    }

    if (countyChanged) {
      setSelectedCounty(nextCounty);
      setSelectedSubCounty(nextSub);
      return;
    }

    // Sub county only — do not touch region/county.
    setSelectedSubCounty(nextSub);
  }, [initialValues.regionId, initialValues.countyId, initialValues.subCountyId]);

  // Load counties from API when region changes.
  useEffect(() => {
    if (!selectedRegion) {
      lastFetchedRegionRef.current = '';
      setCounties([]);
      return;
    }

    if (lastFetchedRegionRef.current === selectedRegion) {
      return;
    }

    let cancelled = false;
    const regionId = selectedRegion;
    lastFetchedRegionRef.current = regionId;

    (async () => {
      setLoading((prev) => ({ ...prev, counties: true }));
      try {
        const response = await locationService.getActiveCounties(null, parseInt(regionId, 10));
        if (!cancelled) {
          setCounties(unwrapLocationList(response));
        }
      } catch (error) {
        console.error('Error loading counties for region:', error);
        if (!cancelled) {
          lastFetchedRegionRef.current = '';
          setCounties([]);
        }
      } finally {
        if (!cancelled) setLoading((prev) => ({ ...prev, counties: false }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRegion]);

  // Sub counties API only when countyId actually changes.
  useEffect(() => {
    if (!selectedCounty) {
      if (lastFetchedCountyRef.current !== '') {
        lastFetchedCountyRef.current = '';
        setSubCounties([]);
      }
      return;
    }

    if (lastFetchedCountyRef.current === selectedCounty) {
      return;
    }

    const cached = subCountiesCacheRef.current.get(selectedCounty);
    if (cached) {
      lastFetchedCountyRef.current = selectedCounty;
      setSubCounties(cached);
      return;
    }

    let cancelled = false;
    const countyId = selectedCounty;
    lastFetchedCountyRef.current = countyId;

    (async () => {
      setLoading((prev) => ({ ...prev, subCounties: true }));
      try {
        const response = await locationService.getActiveSubCounties(parseInt(countyId, 10));
        if (!cancelled) {
          const list = unwrapLocationList(response);
          subCountiesCacheRef.current.set(countyId, list);
          setSubCounties(list);
        }
      } catch (error) {
        console.error('Error loading sub counties:', error);
        if (!cancelled) {
          lastFetchedCountyRef.current = '';
          setSubCounties([]);
        }
      } finally {
        if (!cancelled) setLoading((prev) => ({ ...prev, subCounties: false }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCounty]);

  // Notify parent when selection changes — skip duplicate payloads.
  useEffect(() => {
    const notify = onLocationChangeRef.current;
    if (!notify) return;

    const hasInitial =
      toIdStr(initialValues.regionId) ||
      toIdStr(initialValues.countyId) ||
      toIdStr(initialValues.subCountyId);
    if (hasInitial && !selectedRegion && !selectedCounty && !selectedSubCounty) {
      return;
    }

    const signature = `${selectedCountry}|${selectedRegion}|${selectedCounty}|${selectedSubCounty}`;
    if (signature === lastNotifiedRef.current) {
      return;
    }
    lastNotifiedRef.current = signature;

    notify({
      countryId: selectedCountry ? parseInt(selectedCountry, 10) : KENYA_COUNTRY_ID,
      countryName: KENYA_COUNTRY_NAME,
      regionId: selectedRegion ? parseInt(selectedRegion, 10) : null,
      countyId: selectedCounty ? parseInt(selectedCounty, 10) : null,
      subCountyId: selectedSubCounty ? parseInt(selectedSubCounty, 10) : null,
      regionName:
        locationOptionName(regions.find((r) => String(locationOptionId(r)) === selectedRegion)) ||
        null,
      countyName:
        locationOptionName(counties.find((c) => String(locationOptionId(c)) === selectedCounty)) ||
        null,
      subCountyName:
        locationOptionName(
          subCounties.find((s) => String(locationOptionId(s)) === selectedSubCounty)
        ) || null,
    });
  }, [
    selectedCountry,
    selectedRegion,
    selectedCounty,
    selectedSubCounty,
    counties,
    subCounties,
    regions,
    initialValues.regionId,
    initialValues.countyId,
    initialValues.subCountyId,
  ]);

  const handleRegionChange = (e) => {
    const next = e.target.value;
    lastFetchedRegionRef.current = '';
    lastFetchedCountyRef.current = '';
    setSelectedRegion(next);
    setSelectedCounty('');
    setSelectedSubCounty('');
    setSubCounties([]);
  };

  const handleCountyChange = (e) => {
    const next = e.target.value;
    lastFetchedCountyRef.current = '';
    setSelectedCounty(next);
    setSelectedSubCounty('');
    setSubCounties([]);
  };

  const handleSubCountyChange = (e) => {
    setSelectedSubCounty(e.target.value);
  };

  const selectClass =
    'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10 disabled:opacity-60 disabled:cursor-not-allowed';

  const reqMark = required ? (
    <span className="text-red-500 font-semibold" aria-hidden="true">
      *
    </span>
  ) : null;

  const subCountyReqMark =
    required && subCountyRequired ? (
      <span className="text-red-500 font-semibold" aria-hidden="true">
        *
      </span>
    ) : null;

  const colCount = columns ?? (horizontal ? 4 : 1);
  const effectiveCols = showCountry ? colCount : Math.min(colCount === 1 ? 1 : 3, colCount || 3);
  const layoutClass =
    effectiveCols >= 4
      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'
      : effectiveCols === 3
        ? 'grid grid-cols-1 sm:grid-cols-3 gap-3'
        : effectiveCols === 2
          ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
          : 'space-y-3';

  return (
    <div className={layoutClass}>
      {showCountry && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Country {reqMark}
          </label>
          <div className="relative">
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              disabled={disabled}
              className={selectClass}
            >
              <option value={KENYA_COUNTRY_ID}>{KENYA_COUNTRY_NAME}</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Region {reqMark}
        </label>
        <div className="relative">
          <select
            value={selectedRegion}
            onChange={handleRegionChange}
            disabled={disabled || loadingRegions}
            className={selectClass}
            aria-invalid={showErrors && !!errors.regionId}
          >
            <option value="">{loadingRegions ? 'Loading regions...' : 'Select Region'}</option>
            {regions.map((region) => {
              const idVal = locationOptionId(region);
              return (
                <option key={idVal} value={String(idVal)}>
                  {locationOptionName(region)}
                </option>
              );
            })}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
        {showErrors && errors.regionId && (
          <p className="text-xs text-red-600 mt-1">{errors.regionId}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          County {reqMark}
        </label>
        <div className="relative">
          <select
            value={selectedCounty}
            onChange={handleCountyChange}
            disabled={disabled || !selectedRegion || loading.counties}
            className={selectClass}
            aria-invalid={showErrors && !!errors.countyId}
          >
            <option value="">
              {!selectedRegion ? 'Select Region first' : 'Select County'}
            </option>
            {counties.map((county) => {
              const id = locationOptionId(county);
              return (
                <option key={id} value={id}>
                  {locationOptionName(county)}
                </option>
              );
            })}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
        {showErrors && errors.countyId && (
          <p className="text-xs text-red-600 mt-1">{errors.countyId}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Sub County {subCountyReqMark}
        </label>
        <div className="relative">
          <select
            value={selectedSubCounty}
            onChange={handleSubCountyChange}
            disabled={disabled || !selectedCounty || loading.subCounties}
            className={selectClass}
            aria-invalid={showErrors && !!errors.subCountyId}
          >
            <option value="">
              {!selectedCounty
                ? 'Select County first'
                : subCountyRequired
                  ? 'Select Sub County'
                  : 'Select Sub County (optional)'}
            </option>
            {subCounties.map((sub) => {
              const id = locationOptionId(sub);
              return (
                <option key={id} value={id}>
                  {locationOptionName(sub)}
                </option>
              );
            })}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
        {showErrors && errors.subCountyId && (
          <p className="text-xs text-red-600 mt-1">{errors.subCountyId}</p>
        )}
      </div>
    </div>
  );
};

export default LocationSection;
